import { getApiUrl } from "./api";

export const AUTH_EXPIRED_EVENT = "arta-auth-expired";

export const getStoredAccessToken = () => localStorage.getItem("token") || "";
export const getStoredRefreshToken = () => localStorage.getItem("refresh_token") || "";
export const getStoredRole = () => localStorage.getItem("role") || "";

export const clearStoredAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expires_at");
  localStorage.removeItem("role");
  localStorage.removeItem("username");
};

const notifyAuthExpired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
};

// Refreshing ROTATES the refresh token — the response carries a new one and the old
// is consumed. Two concurrent refreshes therefore send the SAME token, the provider
// honours the first and 401s the rest, and authFetch reads that 401 as "session over"
// and logs the user out. Dashboard5 fires several authenticated requests at once, so
// the instant an access token expires they all 401 together and race exactly this way
// — which is how a session died at almost exactly the 1h token lifetime.
//
// One refresh in flight, shared by every caller.
let refreshInFlight = null;

// Renew this long before expiry instead of waiting for a 401. `expires_at` was being
// written to localStorage and never read, so every renewal was reactive.
const EXPIRY_SKEW_MS = 60_000;

const tokenExpiringSoon = () => {
  const raw = Number(localStorage.getItem("expires_at"));
  if (!Number.isFinite(raw) || raw <= 0) return false;
  // Accept epoch seconds or milliseconds; the field has been written both ways.
  const expiresAtMs = raw < 1e12 ? raw * 1000 : raw;
  return expiresAtMs - Date.now() <= EXPIRY_SKEW_MS;
};

// Returns a DISCRIMINATED result rather than a bare token, because "the refresh
// token is dead" and "a packet was dropped" demand opposite responses: the first
// ends the session, the second must be retried. Collapsing both into `null` is
// safe for authFetch (which just falls through to the request and its 401) but not
// for the socket handshake, where a transient failure read as a dead session logs
// the engineer out mid-call.
const performRefreshDetailed = async () => {
  const refreshToken = getStoredRefreshToken();
  // Nothing to refresh WITH — no retry can change that, so it is terminal.
  if (!refreshToken) return { error: "unauthorized" };

  const role = getStoredRole();
  // `fetch` REJECTS on a network failure rather than returning a response. Since the
  // refresh now runs ahead of the request, an offline blip inside the expiry skew
  // would otherwise make authFetch throw before it even attempted the call — where
  // previously the request went out and merely 401'd. Every concurrent caller shares
  // this promise, so one dropped packet would reject all of them. Treat it as "could
  // not refresh" and let the normal request + 401 path decide.
  let response;
  try {
    response = await fetch(getApiUrl("/api/auth/refresh"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
        role: role || null,
        // Cognito app clients that have a secret must send SECRET_HASH on refresh, and
        // that HMAC covers the username — which a refresh token does not carry. Stored
        // at sign-in for exactly this.
        username: localStorage.getItem("username") || null,
      }),
    });
  } catch {
    return { error: "network" };
  }

  if (!response.ok) {
    // Only the provider rejecting the credential is terminal. A 5xx or a proxy
    // error is the infrastructure talking, not Cognito, and is retryable.
    return { error: response.status === 401 || response.status === 403 ? "unauthorized" : "network" };
  }

  const payload = await response.json().catch(() => ({}));
  const token = payload?.token;
  // A 200 with no token is a server bug, not a rejected credential. Retryable is
  // the non-destructive reading.
  if (!token) return { error: "network" };

  localStorage.setItem("token", token);
  if (payload?.refresh_token) {
    localStorage.setItem("refresh_token", payload.refresh_token);
  }
  if (typeof payload?.expires_at === "number") {
    localStorage.setItem("expires_at", String(payload.expires_at));
  }
  if (payload?.user?.role) {
    localStorage.setItem("role", payload.user.role);
  }
  if (payload?.user?.username) {
    localStorage.setItem("username", payload.user.username);
  }

  return { token };
};

/**
 * Renew the access token. Concurrent callers share one request — see refreshInFlight.
 *
 * The in-flight promise is cleared in `finally`, so a later expiry starts a fresh
 * refresh rather than replaying a stale (already consumed) result.
 *
 * Both public entry points funnel through here so the token- and detail-shaped
 * callers still share ONE POST; a second in-flight refresh would send the already
 * consumed rotating token and 401.
 */
const refreshShared = () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performRefreshDetailed().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const refreshAccessToken = async () => (await refreshShared())?.token || null;

// Same refresh, but says WHY it failed: { token } | { error: "unauthorized" } |
// { error: "network" }. Used where the answer decides between logging the user
// out and retrying — see the copilot's access-denied handling.
export const refreshAccessTokenDetailed = async () => refreshShared();

/**
 * Renew the access token if it is about to expire, and never throw.
 *
 * The Socket.IO handshake sends whatever is in localStorage; an expired token there
 * makes the server treat the socket as anonymous, which the session-ownership guard
 * then refuses. This is the pre-flight for that path, so it swallows everything —
 * a rejection here would take out the connect attempt itself.
 */
export const ensureFreshAccessToken = async () => {
  try {
    if (tokenExpiringSoon() && getStoredRefreshToken()) {
      await refreshAccessToken();
    }
  } catch {
    // Nothing to do: the caller falls back to the stored token and lets the
    // server decide.
  }
  return getStoredAccessToken();
};

// True for links that point at an auth-gated S3 proxy (`/api/images/...` or
// `/api/documents/...`). These cannot be opened with a plain anchor navigation
// because the proxy requires a Bearer token, so they must be fetched and opened
// as a blob instead.
export const isServerProxyDocUrl = (url = "") => {
  if (!url) return false;
  try {
    const p = new URL(url, window.location.origin).pathname;
    return p.includes("/api/images/") || p.includes("/api/documents/");
  } catch {
    return /\/api\/(images|documents)\//.test(url);
  }
};

// Open an auth-gated document (e.g. an S3-proxied citation PDF) in a new tab.
// Uses authFetch for consistent URL resolution and token handling.
// Minimal HTML written into the opened tab so the user sees a loading state
// until the document blob is ready (or an error if the fetch fails).
const docTabHtml = (body) =>
  `<!doctype html><meta charset="utf-8"><title>Document</title>` +
  `<body style="margin:0;font-family:system-ui,sans-serif;display:flex;` +
  `align-items:center;justify-content:center;height:100vh;color:#374151">${body}</body>`;

export const openAuthenticatedFile = async (url) => {
  if (!url) return;
  // Open the tab synchronously within the click gesture to dodge popup blockers.
  const win = window.open("", "_blank");
  // Keep the tab loading until content is ready.
  if (win) win.document.write(docTabHtml("Loading document…"));

  let hash = "";
  const toEndpoint = (u) => {
    try {
      const parsed = new URL(u, window.location.origin);
      hash = parsed.hash || "";
      return parsed.pathname.replace(/['"]+$/, "") + parsed.search;
    } catch {
      const h = u.indexOf("#");
      if (h >= 0) hash = u.slice(h);
      return u;
    }
  };

  try {
    // notifyOnExpiry:false — opening a document must never tear down the app
    // session. A 401 here surfaces as an error in the opened tab, not a logout.
    const response = await authFetch(toEndpoint(url), {}, { notifyOnExpiry: false });
    if (!response.ok) {
      const msg = response.status === 401
        ? "Not authorized to open this document (401)."
        : `Failed to open document (${response.status}).`;
      throw new Error(msg);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const openUrl = objectUrl + hash;
    if (win) {
      win.location.href = openUrl;
    } else {
      window.open(openUrl, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    console.error("Error opening authenticated document:", err);
    const message = err?.message || "Failed to open document";
    // Show the error (with code) in the opened tab; never log the user out.
    if (win && !win.closed) {
      win.document.body.innerHTML =
        `<div style="color:#b91c1c;padding:24px;text-align:center">${message}</div>`;
    } else {
      alert(message);
    }
  }
};

export const authFetch = async (endpoint, init = {}, { retryOn401 = true, notifyOnExpiry = true } = {}) => {
  // Renew BEFORE sending when the token is nearly out, rather than letting the request
  // 401 and repairing afterwards. The reactive path still exists below as a backstop —
  // this just stops a whole screen's worth of parallel requests from all failing at the
  // expiry boundary and then racing to fix it. Deduplicated, so several callers hitting
  // this at once still produce exactly one refresh.
  if (tokenExpiringSoon() && getStoredRefreshToken()) {
    await refreshAccessToken();
  }
  const headers = new Headers(init.headers || {});
  const token = getStoredAccessToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const role = getStoredRole();
  if (role && !headers.has("X-Arta-Role")) {
    headers.set("X-Arta-Role", role);
  }

  const makeRequest = (authHeaders) =>
    fetch(getApiUrl(endpoint), {
      ...init,
      headers: authHeaders,
    });

  let response = await makeRequest(headers);
  if (response.status !== 401 || !retryOn401) {
    return response;
  }

  const newToken = await refreshAccessToken();
  if (!newToken) {
    if (notifyOnExpiry) {
      clearStoredAuth();
      notifyAuthExpired();
    }
    return response;
  }

  const retryHeaders = new Headers(init.headers || {});
  retryHeaders.set("Authorization", `Bearer ${newToken}`);
  const retryRole = getStoredRole();
  if (retryRole) retryHeaders.set("X-Arta-Role", retryRole);
  response = await makeRequest(retryHeaders);
  return response;
};
